import GeneralHelper from '../../00_utilities/helpers/GeneralHelper';
import YoutubeHelper from '../../00_utilities/helpers/YoutubeHelper';
import HTML5VideoHelper from '../../00_utilities/helpers/HTML5VideoHelper';
import VideoHelper from '../../00_utilities/helpers/VideoHelper';
import AccessibilityHelper from '../../00_utilities/helpers/AccessibilityHelper';
import BreakpointHelper from '../../00_utilities/helpers/BreakpointHelper';
import ElementInViewHelper from '../../00_utilities/helpers/ElementInViewHelper';
import CookieHelper from '../../00_utilities/helpers/CookieHelper';

class Video {
    constructor(elem) {
        this.elem = elem;
        this.breakpoints = {
            'screen-xs': 576,
            'screen-sm': 768,
            'screen-md': 992,
        };
        this.boundWindowScrollListener = {};
        this.throttledWindowScrollListener = {};
        this.autoplay = (this.elem.getAttribute('data-autoplay') === 'true'
            || this.elem.getAttribute('data-autoplay') === '1');
        this.videoUrl = this.elem.getAttribute('data-embed');
        this.youtubeId = this.videoUrl ? YoutubeHelper.youTubeGetID(this.videoUrl) : '';
        this.mediaContainer = this.elem.querySelector('.video__media-container');
        this.videoContainer = this.elem.querySelector('.video__container');
        this.showControls = this.elem.getAttribute('data-controls') === 'true'
            || this.elem.getAttribute('data-controls') === '1';
        this.videoSettings = {
            container: this.videoContainer,
            videoURL: this.videoUrl,
            youtubeId: this.videoUrl ? YoutubeHelper.youTubeGetID(this.videoUrl) : '',
            hideControls: !this.showControls,
        };
    }

    init() {
        const prefersReducedMotion = AccessibilityHelper.prefersReducedMotion();
        if (this.elem.classList.contains('video--animate') && !prefersReducedMotion) {
            this.setupScrollAnimations();
        }

        if (this.videoUrl && this.videoUrl.length) {
            if (this.videoUrl.indexOf('youtube') >= 0) {
                this.initializeYoutubeConsent();

                return;
            }
            this.initializeHtmlVideo();
        }
    }

    setupScrollAnimations() {
        this.windowScrollListener();
        this.boundWindowScrollListener = this.windowScrollListener.bind(this);
        this.throttledWindowScrollListener = GeneralHelper.throttle(this.boundWindowScrollListener, 50);
        this.initAnimation();
    }

    initializeYoutubeConsent() {
        const showConsent = YoutubeHelper.showConsent();

        if (!showConsent) {
            YoutubeHelper.displayYoutubeVideo(this.elem);
            this.initializeYoutubeVideo();

            return;
        }

        const playWrapper = this.elem.querySelector('.video__play-wrapper');
        if (playWrapper) {
            playWrapper.setAttribute('tabindex', -1);
        }

        window.addEventListener('consent-type-youtube', () => {
            if (this.autoplay) {
                const options = {
                    threshold: [0.1],
                };
                const observer = new IntersectionObserver((entries, self) => {
                    entries.forEach((entry) => {
                        if (entry.isIntersecting) {
                            if (playWrapper) {
                                playWrapper.removeAttribute('tabindex');
                            }

                            YoutubeHelper.addAndAutoplayVideo(this.videoSettings);
                            self.unobserve(entry.target);
                        }
                    });
                }, options);

                observer.observe(this.elem);
            }
        });

        this.elem.addEventListener('click', () => {
            this.autoplay = 1;
            CookieHelper.setCookie('consent-type-youtube', true);
            YoutubeHelper.dispatchConsentEvent();

            if (playWrapper) {
                playWrapper.removeAttribute('tabindex');
            }
        }, { once: true });
    }

    initializeYoutubeVideo() {
        if (this.autoplay) {
            this.autoplayVideoWhenVisible('youtube');

            return;
        }

        this.elem.addEventListener('click', () => {
            YoutubeHelper.addAndAutoplayVideo(this.videoSettings);
        }, { once: true });
    }

    initializeHtmlVideo() {
        if (this.autoplay) {
            if (this.elem.closest('.lightbox__content')) {
                this.videoContainer = this.elem;
            }
            this.autoplayVideoWhenVisible('mp4');

            return;
        }

        this.elem.addEventListener('click', () => {
            HTML5VideoHelper.addAndAutoplayVideo(this.videoSettings);
        }, { once: true });
    }

    initAnimation() {
        let aboveBreakpoint = BreakpointHelper.aboveBreakpoint(this.breakpoints['screen-sm']);

        if (aboveBreakpoint) {
            window.addEventListener('scroll', this.throttledWindowScrollListener);
        }

        const resizeObserver = new ResizeObserver(() => {
            const breakpointInfo = BreakpointHelper.breakpointCrossed(this.breakpoints['screen-sm'], aboveBreakpoint);
            aboveBreakpoint = breakpointInfo.aboveBreakpoint;
            const { breakpointCrossed } = breakpointInfo;

            if (breakpointCrossed) {
                if (aboveBreakpoint) {
                    window.addEventListener('scroll', this.throttledWindowScrollListener);

                    return;
                }

                window.removeEventListener('scroll', this.throttledWindowScrollListener);
            }
        });
        resizeObserver.observe(this.elem);
    }

    windowScrollListener() {
        const percentageVisibleFromTop = ElementInViewHelper.percentageVisible(this.elem, 'top');
        const percentageVisible = ElementInViewHelper.percentageVisible(this.elem);
        if (percentageVisibleFromTop > 0 && percentageVisibleFromTop <= 100) {
            if (percentageVisible < 20) {
                VideoHelper.stopAll();
            }
            this.updateAnimationScale(percentageVisibleFromTop);
        }
    }

    updateAnimationScale(percentageVisible) {
        const scaleMultiplier = percentageVisible / 100;

        this.elem.style.setProperty('--scale-factor', scaleMultiplier);
    }

    autoplayVideoWhenVisible(type) {
        if (AccessibilityHelper.prefersReducedMotion()) {
            return;
        }

        const options = {
            rootMargin: `${GeneralHelper.getPageHeaderHeight() * -1}px 0px 0px 0px`,
        };

        const observer = new IntersectionObserver((entries, self) => {
            const isIntersecting = typeof entries[0].isIntersecting === 'boolean'
                ? entries[0].isIntersecting : entries[0].intersectionRatio > 0;
            if (isIntersecting) {
                const helper = type === 'youtube' ? YoutubeHelper : HTML5VideoHelper;
                helper.addAndAutoplayVideo(this.videoSettings);

                self.unobserve(this.mediaContainer);
            }
        }, options);

        observer.observe(this.mediaContainer);
    }
}

export default Video;
